import React from 'react'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

import RichTextEditor from 'components_new/molecules/RichTextEditor'
import { RichText } from 'components_new/molecules/RichTextEditor/types'
import SwitchItem from 'components_new/molecules/SwitchItem'

import FieldRow from 'components_new/organisms/internal/MetricStudio/FieldRow'
import {
  MetricField,
  MetricTextsState,
  withTitle
} from 'components_new/organisms/internal/MetricStudio/utils'

interface TextRowProps {
  disabled?: boolean
  edit: boolean
  error?: boolean
  field?: MetricField
  helperText: string
  missing?: boolean
  onChange?: (value: string) => void
  placeholder?: string
  title?: string
  value: string
}

const TextRow = (props: TextRowProps) => {
  const {
    disabled,
    edit,
    error,
    field,
    helperText,
    missing,
    onChange,
    placeholder,
    title,
    value
  } = props

  return (
    <FieldRow
      actions={
        edit ? (
          <TextField
            disabled={disabled}
            error={error}
            fullWidth={true}
            onChange={(event) => onChange?.(event.target.value)}
            placeholder={placeholder}
            size="small"
            sx={{ my: 0 }}
            value={value}
          />
        ) : (
          <Text
            color={value ? 'text.primary' : 'text.secondary'}
            variant="body2"
          >
            {value || '–'}
          </Text>
        )
      }
      field={field}
      helperText={helperText}
      missing={missing}
      title={title}
    />
  )
}

interface RichTextRowProps {
  edit: boolean
  helperText: string
  onChange: (value: RichText | null) => void
  title: string
  value: RichText | null
}

const RichTextRow = (props: RichTextRowProps) => {
  const { edit, helperText, onChange, title, value } = props

  return (
    <FieldRow helperText={helperText} title={title}>
      <RichTextEditor
        edit={edit}
        editHeight={160}
        onChange={onChange}
        placeholder="Skriv här…"
        value={value}
        variant="outlined"
      />
    </FieldRow>
  )
}

interface Props {
  edit: boolean
  missing: MetricField[]
  onChange: (texts: MetricTextsState) => void
  /** Another metric already owns the slug this name would produce. */
  slugTaken: boolean
  texts: MetricTextsState
  writesTexts: boolean
}

/**
 * The whole CMS entry for a metric, so it can be finished here rather than in
 * Contentful. Every text is written in both languages side by side, and the
 * fields marked with a star are the ones a metric cannot be saved without.
 */
const TextsEditor = (props: Props) => {
  const { edit, missing, onChange, slugTaken, texts, writesTexts } = props

  return (
    <>
      {edit && !writesTexts ? (
        <Box sx={{ py: 2 }}>
          <Alert severity="info">
            Texterna skrivs bara till innehållssystemet i produktion. Här sparas
            mätetalet, men texterna följer inte med — mätetalet visas med sitt
            namn tills det skapas i produktion.
          </Alert>
        </Box>
      ) : null}

      <TextRow
        edit={edit}
        field="title_sv"
        helperText="Visas som titel i widgets och i mätetalsbiblioteket."
        missing={missing.includes('title_sv')}
        onChange={(value) => onChange(withTitle(texts, 'sv', value))}
        placeholder="Vidaredebiteringsgrad uppvärmning"
        value={texts.title.sv}
      />
      <TextRow
        edit={edit}
        field="title_en"
        helperText="Titeln på engelska. Slugen skapas ur den."
        missing={missing.includes('title_en')}
        onChange={(value) => onChange(withTitle(texts, 'en', value))}
        placeholder="Recharge rate heating"
        value={texts.title.en}
      />
      <TextRow
        edit={edit}
        field="summary_sv"
        helperText="En mening som förklarar vad mätetalet svarar på."
        missing={missing.includes('summary_sv')}
        onChange={(value) =>
          onChange({ ...texts, summary: { ...texts.summary, sv: value } })
        }
        value={texts.summary.sv}
      />
      <TextRow
        edit={edit}
        field="summary_en"
        helperText="Samma mening på engelska."
        missing={missing.includes('summary_en')}
        onChange={(value) =>
          onChange({ ...texts, summary: { ...texts.summary, en: value } })
        }
        value={texts.summary.en}
      />
      <TextRow
        edit={edit}
        error={slugTaken}
        field="slug"
        helperText={
          slugTaken
            ? 'Ett annat mätetal har redan den här adressen.'
            : 'Adressen på homepal.se. Följer Namn [EN] tills du skriver '
              + 'något eget.'
        }
        missing={missing.includes('slug')}
        onChange={(value) => onChange({ ...texts, slug: value })}
        value={texts.slug}
      />
      <FieldRow
        actions={
          edit ? (
            <SwitchItem
              checked={texts.hidden}
              label="Dold på homepal.se"
              onChange={() => onChange({ ...texts, hidden: !texts.hidden })}
              size="small"
            />
          ) : (
            <Text variant="body2">{texts.hidden ? 'Dold' : 'Listad'}</Text>
          )
        }
        helperText="Mätetalet listas inte i det publika mätetalsbiblioteket, där interna och licensierade mätetal ligger. Styr inte om kunder kan använda mätetalet i portalen."
        title="Dold på homepal.se"
      />

      <RichTextRow
        edit={edit}
        helperText="Vad mätetalet är."
        onChange={(value) =>
          onChange({ ...texts, body: { ...texts.body, sv: value } })
        }
        title="Beskrivning [SE]"
        value={texts.body.sv}
      />
      <RichTextRow
        edit={edit}
        helperText="Lämnas tom om den svenska räcker."
        onChange={(value) =>
          onChange({ ...texts, body: { ...texts.body, en: value } })
        }
        title="Beskrivning [EN]"
        value={texts.body.en}
      />
      <RichTextRow
        edit={edit}
        helperText="Hur mätetalet räknas fram."
        onChange={(value) =>
          onChange({
            ...texts,
            calculation: { ...texts.calculation, sv: value }
          })
        }
        title="Beräkning [SE]"
        value={texts.calculation.sv}
      />
      <RichTextRow
        edit={edit}
        helperText="Lämnas tom om den svenska räcker."
        onChange={(value) =>
          onChange({
            ...texts,
            calculation: { ...texts.calculation, en: value }
          })
        }
        title="Beräkning [EN]"
        value={texts.calculation.en}
      />
      <RichTextRow
        edit={edit}
        helperText="Varför mätetalet är viktigt. Visas bara på homepal.se."
        onChange={(value) =>
          onChange({ ...texts, purpose: { ...texts.purpose, sv: value } })
        }
        title="Syfte [SE]"
        value={texts.purpose.sv}
      />
      <RichTextRow
        edit={edit}
        helperText="Lämnas tom om den svenska räcker."
        onChange={(value) =>
          onChange({ ...texts, purpose: { ...texts.purpose, en: value } })
        }
        title="Syfte [EN]"
        value={texts.purpose.en}
      />
    </>
  )
}

export default TextsEditor
